import { Dispatch, SetStateAction, useMemo } from 'react';
import './QuestionCard.scss';
import { Form } from 'react-bootstrap';
import { Checkbox, Radio } from '@Components/FormCheck';
import { Placement, Size } from '@Components/designConstants';
import { Dropdown } from '../../Components/Dropdown';
import { NestedInput, NestedOptionsConfigMap } from './NestedInput';
import { CheckOption, OnChange, OnChangeHelper } from './fiscalCalConstants';

export type { CheckOption, OnChange, OnChangeHelper } from './fiscalCalConstants';

export const QuestionCardType = {
  RADIO: 'radio',
  DROPDOWN: 'dropdown',
  CHECKBOX: 'checkbox'
} as const;
export type QuestionCardTypeValue = (typeof QuestionCardType)[keyof typeof QuestionCardType];

export interface QuestionProps {
  title?: string;
  subtitle?: string;
  type: QuestionCardTypeValue;
  options?: { value: string | number | boolean; label: string }[];
  readOnly?: boolean;
  nestedOptionsConfig?: NestedOptionsConfigMap;
  onChange: OnChange | Dispatch<SetStateAction<any>>;
  formState: any;
  formStateKey: string | null;
  checkOption?: CheckOption;
  onChangeHelper?: OnChangeHelper;
  alwasyShowNestedQuestion?: boolean;
  note?: string | string[];
}

export const Question = ({
  title,
  subtitle,
  type,
  options = [],
  readOnly = false,
  nestedOptionsConfig = {},
  onChange,
  formState,
  formStateKey,
  checkOption,
  onChangeHelper,
  alwasyShowNestedQuestion,
  note
}: QuestionProps) => {
  const question = useMemo(() => {
    const makeNestedInput = (value, config, isChecked, show) =>
      config[`${value}`] != null && (isChecked || show) ? (
        <NestedInput
          key={`${type}NestedInput${value}`}
          config={config[`${value}`]}
          readOnly={readOnly}
          onChange={onChange}
          formState={
            config[`${value}`].formState !== undefined ? config[`${value}`].formState : formState
          }
          disabled={!isChecked}
        />
      ) : null;
    switch (type) {
      case QuestionCardType.RADIO:
        return (
          <div className={`question-input-fields ${readOnly ? 'read-only' : ''}`}>
            {options.map((option, index) => {
              const { value, label } = option;
              const isChecked =
                formStateKey == null ? formState === value : formState[formStateKey] === value;
              const resolvedOnchange =
                formStateKey == null
                  ? () => onChange(value)
                  : () => onChange(value, formStateKey, onChangeHelper);
              return (
                <div key={`radioOption${index}`} className='radio-option'>
                  <Radio
                    id={`${value}-radio`}
                    label={label}
                    checked={isChecked}
                    onChange={resolvedOnchange}
                    disabled={readOnly}
                  />
                  {makeNestedInput(value, nestedOptionsConfig, isChecked, alwasyShowNestedQuestion)}
                </div>
              );
            })}
          </div>
        );
      case QuestionCardType.DROPDOWN: {
        const value = formStateKey == null ? formState : formState[formStateKey];
        const resolvedOnchange =
          formStateKey == null
            ? () => onChange(value)
            : () => onChange(value, formStateKey, onChangeHelper);
        return (
          <div className={`question-input-fields ${readOnly ? 'read-only' : ''}`}>
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              size={Size.LG}
              options={options}
              value={value}
              disabled={readOnly}
              onChange={resolvedOnchange}
            />
            {makeNestedInput(
              value,
              nestedOptionsConfig,
              nestedOptionsConfig[value],
              alwasyShowNestedQuestion
            )}
          </div>
        );
      }
      case QuestionCardType.CHECKBOX: {
        return (
          <div className={`question-input-fields ${readOnly ? 'read-only' : ''}`}>
            <Form.Group key='checkboxinputgroup'>
              {options.map((option, index) => {
                const { value, label } = option;
                let isChecked: boolean;
                if (checkOption) {
                  isChecked = Boolean(checkOption(value));
                } else if (formStateKey == null) {
                  isChecked = formState === value;
                } else {
                  isChecked = formState[formStateKey] === value;
                }
                const resolvedOnchange =
                  formStateKey == null
                    ? () => onChange(value)
                    : () => onChange(value, formStateKey, onChangeHelper);
                return (
                  <div key={`checkboxOption${index}`} className='checkbox-option'>
                    <Checkbox
                      id={`${value}-checkbox`}
                      label={label}
                      checked={isChecked}
                      onChange={resolvedOnchange}
                      disabled={readOnly}
                    />
                    {makeNestedInput(
                      value,
                      nestedOptionsConfig,
                      isChecked,
                      alwasyShowNestedQuestion
                    )}
                  </div>
                );
              })}
            </Form.Group>
          </div>
        );
      }
      default:
        return null;
    }
  }, [
    formState,
    formStateKey,
    nestedOptionsConfig,
    onChange,
    options,
    readOnly,
    type,
    checkOption,
    onChangeHelper,
    alwasyShowNestedQuestion
  ]);

  return (
    <div className='card-content'>
      <div className='question-title'>{title}</div>
      {subtitle && <p className='card-subtitle'>{subtitle}</p>}
      {question}
      {note && (
        <div className='nested-input-note'>
          {Array.isArray(note)
            ? note.map(n => (
                <div key={`noteItem${title}${n}`} className='nested-note-item'>
                  {n}
                </div>
              ))
            : note}
        </div>
      )}
    </div>
  );
};
